import { useState, useEffect, useCallback, useRef } from "react";

export function extractBundleName(html: string): string | null {
  if (!html) return null;
  const match = html.match(/\/assets\/index-[A-Za-z0-9_-]+\.js/);
  return match ? match[0] : null;
}

export function getCurrentBundleName(): string | null {
  if (typeof document === "undefined") return null;
  const scripts = Array.from(document.querySelectorAll("script[src]"));
  for (const s of scripts) {
    const src = s.getAttribute("src") || "";
    if (src.includes("/assets/index-")) {
      return extractBundleName(src) || src;
    }
  }
  return null;
}

export interface NewVersionState {
  isNewVersionAvailable: boolean;
  reload: () => void;
}

export function useNewVersionCheck(intervalMs = 10 * 60 * 1000): NewVersionState {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const currentBundleRef = useRef<string | null>(null);

  const checkForUpdate = useCallback(async () => {
    if (typeof window === "undefined" || typeof fetch === "undefined") return;

    try {
      if (!currentBundleRef.current) {
        currentBundleRef.current = getCurrentBundleName();
      }

      // If we cannot detect the current bundle (e.g. in dev server), don't check
      if (!currentBundleRef.current) return;

      const res = await fetch("/", { cache: "no-store" });
      if (!res.ok) return;

      const html = await res.text();
      const latestBundle = extractBundleName(html);

      if (latestBundle && latestBundle !== currentBundleRef.current) {
        setIsNewVersionAvailable(true);
      }
    } catch {
      // Ignore network errors during background check
    }
  }, []);

  useEffect(() => {
    // Initial bundle discovery
    currentBundleRef.current = getCurrentBundleName();

    const interval = setInterval(checkForUpdate, intervalMs);
    const handleFocus = () => {
      void checkForUpdate();
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
    };
  }, [checkForUpdate, intervalMs]);

  const reload = useCallback(() => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  return { isNewVersionAvailable, reload };
}
